const ALLOWED_TAGS = new Set([
  'P',
  'BR',
  'DIV',
  'STRONG',
  'B',
  'EM',
  'I',
  'U',
  'UL',
  'OL',
  'LI',
  'A',
  'SPAN',
  'IMG',
  'FONT',
  'H1',
  'H2',
  'H3',
  'BLOCKQUOTE',
]);

const HTML_TAG = /<\s*\/?\s*[a-zA-Z][^>]*>/;
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const RGB_COLOR = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i;
const NAMED_COLOR = /^[a-zA-Z]{3,20}$/;
const SAFE_IMAGE =
  /^(https?:\/\/[^\s"'<>]+|\/api\/notice\/image\/[a-f0-9]{32}\.(?:jpg|png|gif|webp))$/i;
const SAFE_LINK = /^https?:\/\/[^\s"'<>]+$/i;

export const looksLikeHtml = (content: string) => HTML_TAG.test(content);

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export const safeColor = (color: string | null | undefined) => {
  if (!color) return null;
  const value = color.trim();
  if (HEX_COLOR.test(value) || NAMED_COLOR.test(value)) return value;
  const rgb = value.match(RGB_COLOR);
  if (!rgb) return null;
  if (rgb.slice(1, 4).some((channel) => Number(channel) > 255)) return null;
  return value.replace(/\s+/g, '');
};

const colorFromStyle = (style: string | null) => {
  if (!style) return null;
  const matched = style.match(/(?:^|;)\s*color\s*:\s*([^;]+)/i);
  return safeColor(matched?.[1]);
};

const appendCleaned = (parent: Node, node: Node | null) => {
  if (!node) return;
  parent.appendChild(node);
};

const cleanNode = (node: Node): Node | null => {
  if (node.nodeType === Node.TEXT_NODE) {
    return document.createTextNode(node.textContent ?? '');
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const element = node as HTMLElement;
  const tag = element.tagName.toUpperCase();
  if (['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META'].includes(tag)) {
    return null;
  }

  if (tag === 'FONT') {
    const span = document.createElement('span');
    const color = safeColor(element.getAttribute('color'));
    if (color) span.setAttribute('style', `color:${color}`);
    Array.from(element.childNodes).forEach((child) => appendCleaned(span, cleanNode(child)));
    return span;
  }

  if (!ALLOWED_TAGS.has(tag)) {
    const fragment = document.createDocumentFragment();
    Array.from(element.childNodes).forEach((child) => appendCleaned(fragment, cleanNode(child)));
    return fragment;
  }

  const cleaned = document.createElement(tag.toLowerCase());
  if (tag === 'A') {
    const href = element.getAttribute('href') ?? '';
    if (SAFE_LINK.test(href)) {
      cleaned.setAttribute('href', href);
      cleaned.setAttribute('target', '_blank');
      cleaned.setAttribute('rel', 'noopener noreferrer');
    }
  }
  if (tag === 'IMG') {
    const src = element.getAttribute('src') ?? '';
    if (!SAFE_IMAGE.test(src)) return null;
    cleaned.setAttribute('src', src);
    const alt = (element.getAttribute('alt') ?? '').slice(0, 80);
    if (alt) cleaned.setAttribute('alt', alt);
  }
  if (tag === 'SPAN') {
    const color = colorFromStyle(element.getAttribute('style'));
    if (color) cleaned.setAttribute('style', `color:${color}`);
  }

  Array.from(element.childNodes).forEach((child) => appendCleaned(cleaned, cleanNode(child)));
  return cleaned;
};

export const sanitizeNoticeHtml = (input: string) => {
  if (!input || !looksLikeHtml(input)) return input?.trim?.() ? input : '';
  const parsed = new DOMParser().parseFromString(input, 'text/html');
  const root = document.createElement('div');
  Array.from(parsed.body.childNodes).forEach((child) => appendCleaned(root, cleanNode(child)));
  return root.innerHTML;
};

export const toEditorHtml = (content: string) => {
  if (!content) return '';
  if (looksLikeHtml(content)) return sanitizeNoticeHtml(content);
  return content
    .split('\n')
    .map((line) => `<p>${escapeHtml(line) || '<br>'}</p>`)
    .join('');
};

export const isEmptyNotice = (content: string) => {
  if (!content || !content.trim()) return true;
  if (!looksLikeHtml(content)) return false;
  const parsed = new DOMParser().parseFromString(content, 'text/html');
  const text = (parsed.body.textContent ?? '').replace(/\u00a0/g, ' ').trim();
  const hasImage = Array.from(parsed.body.querySelectorAll('img')).some((image) =>
    SAFE_IMAGE.test(image.getAttribute('src') ?? ''),
  );
  return text === '' && !hasImage;
};

export const BASIC_NOTICE_TEMPLATE = [
  '<p><strong>안내</strong></p>',
  '<p>내용을 입력해 주세요.</p>',
  '<ul><li>첫 번째 항목</li><li>두 번째 항목</li></ul>',
  '<p>자세한 내용은 <a href="https://example.com">관련 링크</a>를 확인해 주세요.</p>',
].join('');
